import React, { useEffect, useMemo, useState } from 'react';
import {
  HiOutlineCurrencyDollar,
  HiOutlinePlus,
  HiOutlineBellAlert,
  HiOutlineWrenchScrewdriver,
  HiOutlinePencil,
  HiOutlineTrash,
} from 'react-icons/hi2';
import SharedModal from '../components/common/SharedModal';
import { sparePartsService, machineService, masterDataService, authService } from '../services';
import { usePermissions } from '../hooks/usePermissions';
import { toast } from 'react-toastify';
import PageLoading from '../components/common/PageLoading';
import './SpareParts.css';

const defaultForm = {
  partNumber: '',
  partName: '',
  vendor: '',
  uom: '',
  minimumStock: 0,
  reorderPoint: '',
  leadTimeDays: '',
  status: 'Active',
};

const defaultTx = {
  transactionType: 'Purchase',
  quantity: 1,
  unitCost: '',
  totalCost: '',
  transactionDate: '',
  workOrderId: '',
  machineId: '',
  shiftCode: '',
  remarks: '',
};

const SpareParts = () => {
  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();

  // Permission checks
  const canView = hasPermission('SpareParts.View');
  const canCreate = hasPermission('SpareParts.Create');
  const canEdit = hasPermission('SpareParts.Edit');
  const canDelete = hasPermission('SpareParts.Delete');

  console.log('canView', canView);
  console.log('canCreate', canCreate);
  console.log('canEdit', canEdit);
  console.log('canDelete', canDelete);

  const [parts, setParts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [costSummary, setCostSummary] = useState(null);
  const [machines, setMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [showTx, setShowTx] = useState(false);
  const [txPart, setTxPart] = useState(null);
  const [txForm, setTxForm] = useState(defaultTx);
  const [txSaving, setTxSaving] = useState(false);

  useEffect(() => {
    if (tenantId && canView) {
      loadAll();
      loadLookups();
    } else if (!canView) {
      const msg = 'You do not have permission to view Spare Parts records';
      setError(msg);
      toast.error(msg);
      setLoading(false);
    } else {
      const msg = 'Please login to view spare parts';
      setError(msg);
      toast.error(msg);
      setLoading(false);
    }
  }, [tenantId, canView]);

  const loadLookups = async () => {
    try {
      const tenantId = authService.getTenantId();
      const [machineRes, shiftRes] = await Promise.all([
        machineService.getAllMachines(),
        masterDataService.getShifts(tenantId),
      ]);
      setMachines(machineRes || []);
      setShifts(shiftRes || []);
    } catch (err) {
      console.error('Failed to load lookups', err);
    }
  };

  const loadAll = async () => {
    try {
      setLoading(true);
      setError('');
      const [partsRes, alertsRes, costRes] = await Promise.all([
        sparePartsService.getParts(),
        sparePartsService.getAlerts(),
        sparePartsService.getCostSummary(),
      ]);
      setParts(partsRes || []);
      setAlerts(alertsRes || []);
      setCostSummary(costRes || null);
    } catch (err) {
      console.error(err);
      const msg = 'Failed to load spare parts data.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const filteredParts = useMemo(() => {
    return parts.filter((p) => {
      const term = search.toLowerCase();
      return (
        p.partName?.toLowerCase().includes(term) ||
        p.partNumber?.toLowerCase().includes(term) ||
        p.vendor?.toLowerCase().includes(term)
      );
    });
  }, [parts, search]);

  const validateForm = () => {
    const errors = {};
    if (!form.partNumber.trim()) errors.partNumber = 'Part number is required';
    if (!form.partName.trim()) errors.partName = 'Part name is required';
    if (form.minimumStock < 0) errors.minimumStock = 'Minimum stock cannot be negative';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSavePart = async (e) => {
    e.preventDefault();

    // Check permissions before submitting
    if (editing && !canEdit) {
      setError('You do not have permission to edit Spare Parts records');
      return;
    }
    if (!editing && !canCreate) {
      setError('You do not have permission to create Spare Parts records');
      return;
    }

    if (!validateForm()) return;

    try {
      setSaving(true);
      setError('');
      const payload = {
        partNumber: form.partNumber.trim(),
        partName: form.partName.trim(),
        vendor: form.vendor.trim() || null,
        uom: form.uom.trim() || null,
        minimumStock: Number(form.minimumStock) || 0,
        reorderPoint: form.reorderPoint ? Number(form.reorderPoint) : null,
        leadTimeDays: form.leadTimeDays ? Number(form.leadTimeDays) : null,
        status: form.status || 'Active',
      };

      if (editing) {
        await sparePartsService.updatePart(editing.sparePartId, payload);
      } else {
        await sparePartsService.createPart(payload);
      }

      setShowForm(false);
      setEditing(null);
      setForm(defaultForm);
      await loadAll();
      toast.success(editing ? 'Spare part updated successfully!' : 'Spare part created successfully!');
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.message || 'Failed to save spare part.';
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (part) => {
    if (!canEdit) {
      setError('You do not have permission to edit Spare Parts records');
      return;
    }
    setEditing(part);
    setForm({
      partNumber: part.partNumber || '',
      partName: part.partName || '',
      vendor: part.vendor || '',
      uom: part.uom || '',
      minimumStock: part.minimumStock ?? 0,
      reorderPoint: part.reorderPoint ?? '',
      leadTimeDays: part.leadTimeDays ?? '',
      status: part.status || 'Active',
    });
    setFormErrors({});
    setShowForm(true);
  };

  const openCreate = () => {
    if (!canCreate) {
      setError('You do not have permission to create Spare Parts records');
      return;
    }
    setEditing(null);
    setForm(defaultForm);
    setFormErrors({});
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!canDelete) {
      setError('You do not have permission to delete Spare Parts records');
      return;
    }
    if (!window.confirm('Archive this spare part?')) return;
    try {
      await sparePartsService.deletePart(id);
      await loadAll();
      toast.success('Spare part archived successfully!');
    } catch (err) {
      console.error(err);
      const msg = 'Failed to delete spare part.';
      setError(msg);
      toast.error(msg);
    }
  };

  const openTx = (part) => {
    setTxPart(part);
    setTxForm({ ...defaultTx, transactionDate: new Date().toISOString().slice(0, 16) });
    setShowTx(true);
  };

  const handleSaveTx = async (e) => {
    e.preventDefault();
    if (!txPart) return;

    if (Number(txForm.quantity) === 0 || Number.isNaN(Number(txForm.quantity))) {
      setError('Quantity must be non-zero.');
      return;
    }

    try {
      setTxSaving(true);
      setError('');
      const payload = {
        transactionType: txForm.transactionType,
        quantity: Number(txForm.quantity),
        unitCost: txForm.unitCost ? Number(txForm.unitCost) : null,
        totalCost: txForm.totalCost ? Number(txForm.totalCost) : null,
        transactionDate: txForm.transactionDate ? new Date(txForm.transactionDate).toISOString() : null,
        workOrderId: txForm.workOrderId ? Number(txForm.workOrderId) : null,
        machineId: txForm.machineId ? Number(txForm.machineId) : null,
        shiftCode: txForm.shiftCode || null,
        remarks: txForm.remarks || null,
      };

      await sparePartsService.recordTransaction(txPart.sparePartId, payload);
      setShowTx(false);
      setTxPart(null);
      setTxForm(defaultTx);
      await loadAll();
      toast.success('Transaction recorded successfully!');
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.message || 'Failed to record transaction.';
      setError(message);
      toast.error(message);
    } finally {
      setTxSaving(false);
    }
  };

  const renderStatus = (status) => {
    const cls = status?.toLowerCase() === 'active' ? 'status-active' : 'status-inactive';
    return <span className={cls}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="spare-parts-page">
        <PageLoading message="Loading spare parts..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer spare-parts-page">
      <div className="page-header">
        <div className='header-contain'>
          <h1>Spare Parts & Inventory</h1>
          <p>Track parts, stock movements, alerts, and costs</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary btn" onClick={loadAll}>
            Refresh
          </button>
          {canCreate && (
            <button className="btn-primary btn" onClick={openCreate}>
              <HiOutlinePlus /> Add Part
            </button>
          )}
        </div>
      </div>

      {error && <div className="card" style={{ color: '#d93025' }}>{error}</div>}

      <div className="cards">
        <div className="card">
          <h4><HiOutlineBellAlert /> Alerts</h4>
          {alerts.length === 0 ? (
            <div className="empty">No alerts</div>
          ) : (
            <div className="alert-list">
              {alerts.map((a) => (
                <div key={`${a.sparePartId}-${a.alertType}`} className={`card alert-card ${a.alertType === 'LowStock' ? 'low' : ''}`}>
                  <strong>{a.partNumber} - {a.partName}</strong>
                  <div className='flexbox'>
                    <div>Type: {a.alertType}</div>
                    <div>Stock {a.currentStock} / Min {a.minimumStock}</div>
                  </div>
                  {a.notes && <div className="badge">{a.notes}</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <h4><HiOutlineCurrencyDollar /> Cost Summary</h4>
          {!costSummary ? (
            <div className="empty">No issue costs yet</div>
          ) : (
            <div className="summary-grid">
              <div><strong>Total:</strong> {costSummary.totalCost?.toFixed(2) ?? '0.00'}</div>
              <div><strong>Machines:</strong> {Object.keys(costSummary.costByMachine || {}).length}</div>
              <div><strong>Breakdowns:</strong> {Object.keys(costSummary.costByBreakdown || {}).length}</div>
              <div><strong>Shifts:</strong> {Object.keys(costSummary.costByShift || {}).length}</div>
            </div>
          )}
        </div>
      </div>

      <div className="table-container pad10 kizantable">
        <div className="table-header">
          <div className="table-info">{filteredParts.length} items</div>
          <div className="search-box"  >
            <input
              type="text"
              placeholder="Search spare parts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

        </div>

        <table>
          <thead>
            <tr>
              <th>Part</th>
              <th>Vendor</th>
              <th>UOM</th>
              <th className="numeric">Stock</th>
              <th className="numeric">Min</th>
              <th>Status</th>
              <th style={{ width: 140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredParts.length === 0 && (
              <tr>
                <td colSpan="7" className="empty">No spare parts found</td>
              </tr>
            )}
            {filteredParts.map((p) => (
              <tr key={p.sparePartId}>
                <td>
                  <div><strong>{p.partNumber}</strong></div>
                  <div>{p.partName}</div>
                </td>
                <td>{p.vendor || '-'}</td>
                <td>{p.uom || '-'}</td>
                <td className="numeric">{p.currentStock ?? 0}</td>
                <td className="numeric">{p.minimumStock ?? 0}</td>
                <td>{renderStatus(p.status)}</td>
                <td>
                  <div className="inline-actions">
                    <button className="btn-secondary" onClick={() => openTx(p)} title="Record transaction">
                      <HiOutlineWrenchScrewdriver />
                    </button>
                    {canEdit && (
                      <button className="btn-secondary" onClick={() => openEdit(p)} title="Edit">
                        <HiOutlinePencil />
                      </button>
                    )}
                    {canDelete && (
                      <button className="btn-danger" onClick={() => handleDelete(p.sparePartId)} title="Archive">
                        <HiOutlineTrash />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditing(null); }}
        title={editing ? 'Edit Spare Part' : 'Add New Spare Part'}
        customWidth="800px"
        footer={
          <div className="modal-actions" style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', width: '100%'}}>
            <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" form="spare-part-form" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </button>
          </div>
        }
      >
        <div className="shared-modal-body">
          <form id="spare-part-form" onSubmit={handleSavePart} className="modal-form">
            <div className='row'>
              <div className="form-group col-6">
                <label>Part Number <span className="required">*</span></label>
                <input
                  value={form.partNumber}
                  onChange={(e) => setForm({ ...form, partNumber: e.target.value })}
                  placeholder="e.g. SP-1001"
                  className={formErrors.partNumber ? 'error-input' : ''}
                />
                {formErrors.partNumber && <div className="error-text">{formErrors.partNumber}</div>}
              </div>

              <div className="form-group col-6">
                <label>Part Name <span className="required">*</span></label>
                <input
                  value={form.partName}
                  onChange={(e) => setForm({ ...form, partName: e.target.value })}
                  placeholder="e.g. Hydraulic Filter"
                  className={formErrors.partName ? 'error-input' : ''}
                />
                {formErrors.partName && <div className="error-text">{formErrors.partName}</div>}
              </div>

              <div className="form-group col-6">
                <label>Vendor</label>
                <input
                  value={form.vendor}
                  onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                  placeholder="Supplier Name"
                />
              </div>

              <div className="form-group col-6">
                <label>Unit of Measure (UOM)</label>
                <input
                  value={form.uom}
                  onChange={(e) => setForm({ ...form, uom: e.target.value })}
                  placeholder="e.g. pcs, kg, ltr"
                />
              </div>

              <div className="form-group col-6">
                <label>Minimum Stock <span className="required">*</span></label>
                <input
                  type="number"
                  value={form.minimumStock}
                  onChange={(e) => setForm({ ...form, minimumStock: e.target.value })}
                  min="0"
                />
                {formErrors.minimumStock && <div className="error-text">{formErrors.minimumStock}</div>}
              </div>

              <div className="form-group col-6">
                <label>Reorder Point</label>
                <input
                  type="number"
                  value={form.reorderPoint}
                  onChange={(e) => setForm({ ...form, reorderPoint: e.target.value })}
                  min="0"
                />
              </div>

              <div className="form-group col-6">
                <label>Lead Time (Days)</label>
                <input
                  type="number"
                  value={form.leadTimeDays}
                  onChange={(e) => setForm({ ...form, leadTimeDays: e.target.value })}
                  min="0"
                />
              </div>

              <div className="form-group col-6">
                <label>Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Transaction Modal */}
      <SharedModal
        isOpen={showTx && !!txPart}
        onClose={() => setShowTx(false)}
        title={`Record Transaction - ${txPart?.partNumber}`}
        customWidth="800px"
        footer={
          <div className="modal-actions" style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', width: '100%'}}>
            <button type="button" className="btn-secondary" onClick={() => setShowTx(false)}>Cancel</button>
            <button type="submit" form="transaction-form" className="btn-primary" disabled={txSaving}>
              {txSaving ? 'Saving...' : 'Save Transaction'}
            </button>
          </div>
        }
      >
        <div className="shared-modal-body">
          <form id="transaction-form" onSubmit={handleSaveTx} className='modal-form'>
            <div className="row">
              <div className="form-group col-6">
                <label>Type</label>
                <select
                  value={txForm.transactionType}
                  onChange={(e) => setTxForm({ ...txForm, transactionType: e.target.value })}
                >
                  <option>Purchase</option>
                  <option>Issue</option>
                  <option>Adjustment</option>
                </select>
              </div>
              <div className="form-group col-6">
                <label>Quantity</label>
                <input
                  type="number"
                  value={txForm.quantity}
                  onChange={(e) => setTxForm({ ...txForm, quantity: e.target.value })}
                />
              </div>
              <div className="form-group col-6">
                <label>Unit Cost</label>
                <input
                  type="number"
                  value={txForm.unitCost}
                  onChange={(e) => setTxForm({ ...txForm, unitCost: e.target.value })}
                />
              </div>
              <div className="form-group col-6">
                <label>Total Cost</label>
                <input
                  type="number"
                  value={txForm.totalCost}
                  onChange={(e) => setTxForm({ ...txForm, totalCost: e.target.value })}
                />
              </div>
              <div className="form-group col-6">
                <label>Transaction Date</label>
                <input
                  type="datetime-local"
                  value={txForm.transactionDate}
                  onChange={(e) => setTxForm({ ...txForm, transactionDate: e.target.value })}
                />
              </div>
              <div className="form-group col-6">
                <label>Work Order ID</label>
                <input
                  type="number"
                  value={txForm.workOrderId}
                  onChange={(e) => setTxForm({ ...txForm, workOrderId: e.target.value })}
                />
              </div>
              <div className="form-group col-6">
                <label>Machine</label>
                <select
                  value={txForm.machineId}
                  onChange={(e) => setTxForm({ ...txForm, machineId: e.target.value })}
                >
                  <option value="">Select machine</option>
                  {machines.map((m) => (
                    <option key={m.machineId} value={m.machineId}>
                      {m.machineCode || m.machineId} - {m.machineName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group col-6">
                <label>Shift</label>
                <select
                  value={txForm.shiftCode}
                  onChange={(e) => setTxForm({ ...txForm, shiftCode: e.target.value })}
                >
                  <option value="">Select shift</option>
                  {shifts.map((s) => (
                    <option key={s.shiftId} value={s.shiftCode}>
                      {s.shiftCode} - {s.shiftName || s.shiftCode}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group col-12">
                <label>Remarks</label>
                <textarea
                  rows="2"
                  value={txForm.remarks}
                  onChange={(e) => setTxForm({ ...txForm, remarks: e.target.value })}
                />
              </div>
            </div>
          </form>
        </div>
      </SharedModal>
    </div >
  );
};

export default SpareParts;